import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Writable } from 'type-fest';
import { Updater } from 'use-immer';
import { z } from 'zod';

import { NonEmptyArray, SimpleZodOutput } from '_lib/api/apiTypes.shim';
import { Button } from '_lib/components/Button/Button';
import { FlexBox, FlexBoxProps } from '_lib/components/FlexBox/FlexBox';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import {
  TextFieldFormItem,
  PhoneNumberTextFieldFormItem,
  TextFieldFormItemProps
} from '_lib/components/TextFieldFormItem/TextFieldFormItem';

const SEARCH_FIELDS = ['address', 'name', 'phoneNumber'] as const;
type AllSearchFields = Writable<typeof SEARCH_FIELDS>;
type SearchField = AllSearchFields[number];
type SearchFields = NonEmptyArray<SearchField>;

const SearchFieldLabels = { address: 'Address', name: 'Name', phoneNumber: 'Phone' } as const satisfies Record<
  SearchField,
  string
>;

const FormSearchSchema = <T extends SearchFields>(searchFields: T) =>
  z.object({
    searchField: z.enum(searchFields),
    value: z
      .string({ required_error: 'Required', invalid_type_error: 'Required' })
      .min(2, { message: 'Must be 2 or more characters' })
  });

type FormSearchItem<T extends SearchFields> = SimpleZodOutput<ReturnType<typeof FormSearchSchema<T>>>;

const getSearchTextComponent = <T extends SearchField>(
  fieldName: T,
  props: TextFieldFormItemProps<FormSearchItem<NonEmptyArray<T>>>
) => (fieldName === 'phoneNumber' ? <PhoneNumberTextFieldFormItem {...props} /> : <TextFieldFormItem {...props} />);

export type CustomerSearchItem = {
  searchField: SearchField;
  value?: string;
  isActive?: boolean;
  isLastSearched?: boolean;
};

type CustomerSearchProps = {
  searchItems: CustomerSearchItem[];
  onUpdateSearchItems: Updater<CustomerSearchItem[]>;
  onCustomerSearch: (searchField: SearchField) => void;
  disabled?: boolean;
  sx?: FlexBoxProps['sx'];
};

// TODO: use this component in ST API integration
export const CustomerSearch = (props: CustomerSearchProps) => {
  const { searchItems, onUpdateSearchItems, onCustomerSearch, disabled, sx } = props;

  const activeItem = searchItems.find((item) => item.isActive);

  const { getValues, handleSubmit, reset, control } = useForm<FormSearchItem<SearchFields>>({
    resolver: zodResolver(FormSearchSchema([...SEARCH_FIELDS])),
    defaultValues: activeItem
      ? { searchField: activeItem.searchField, value: activeItem.value }
      : { searchField: 'phoneNumber', value: '' },
    mode: 'onSubmit'
  });

  const _submit = handleSubmit((customerSearch) => {
    onCustomerSearch(customerSearch.searchField);
    onUpdateSearchItems((prevState) => {
      prevState.forEach((item) => {
        if (item.searchField === customerSearch.searchField) item.isLastSearched = true;
        else item.isLastSearched = false;
      });
    });
  });

  const onFieldNameUpdate = (newFieldName: SearchField) => {
    onUpdateSearchItems((prevState) => {
      prevState.forEach((item) => {
        if (item.searchField === newFieldName) {
          item.isActive = true;
          reset({ searchField: item.searchField, value: item.value });
        } else {
          item.isActive = false;
        }
      });
    });
  };

  const onClear = () => {
    onUpdateSearchItems((prevState) => {
      prevState.forEach((item) => {
        if (item.isActive) {
          item.value = '';
          reset({ searchField: item.searchField, value: '' });
        }
      });
    });
  };

  return (
    <FlexBox paddingX={2} paddingY={1} sx={sx}>
      <FlexBox flexDirection='row' flexGrow={1} gap={1}>
        <SelectFormItem
          name='searchField'
          label='Category'
          fullWidth={false}
          variant='outlined'
          sx={{ minWidth: 100 }}
          control={control}
          defaultValue={'phoneNumber'}
          required
          onChange={(e) => onFieldNameUpdate(e.target.value as SearchField)}>
          {(searchItems.map((item) => item.searchField) || SEARCH_FIELDS).map((searchField) => (
            <MenuItem key={searchField} value={searchField}>
              {SearchFieldLabels[searchField]}
            </MenuItem>
          ))}
        </SelectFormItem>
        {getSearchTextComponent(getValues('searchField'), {
          control,
          name: 'value',
          label: 'Search For',
          required: true,
          fullWidth: false,
          variant: 'outlined',
          sx: { flexGrow: 1 },
          slotProps: {
            htmlInput: { maxLength: 50 }
          },
          onChange: (e) => {
            onUpdateSearchItems((prevState) => {
              prevState.forEach((item) => {
                if (item.isActive && item.value !== e.target.value) {
                  item.isLastSearched = false;
                  item.value = e.target.value;
                }
              });
            });
          },
          onEnter: !disabled ? _submit : undefined
        })}
        <Button
          variant='outlined'
          color='error'
          onClick={onClear}
          text='Clear'
          size='medium'
          sx={{ marginTop: 1, maxHeight: 40, maxWidth: 60 }}
        />
        <Button
          disabled={disabled}
          variant='outlined'
          color='primary'
          onClick={_submit}
          text='Search'
          size='medium'
          sx={{ marginTop: 1, maxHeight: 40, maxWidth: 60 }}
        />
      </FlexBox>
    </FlexBox>
  );
};
